/**
 * WHIP (WebRTC-HTTP Ingestion Protocol) クライアント実装
 * SRS と OvenMediaEngine (OME) の両方に対応
 */

import type { QualityPreset, WHIPConnectionState, WHIPStats } from './types';

export interface WHIPClientConfig {
  serverType: 'srs' | 'ome';
  streamKey: string;
  qualityPreset: QualityPreset;
  onConnectionStateChange?: (state: WHIPConnectionState) => void;
  onStatsUpdate?: (stats: WHIPStats) => void;
}

export class WHIPClient {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private whipSessionUrl: string | null = null;
  private statsInterval: ReturnType<typeof setInterval> | null = null;
  private keyFrameInterval: ReturnType<typeof setInterval> | null = null;
  private config: WHIPClientConfig;

  constructor(config: WHIPClientConfig) {
    this.config = config;
  }

  /**
   * WHIP配信を開始
   */
  async start(stream: MediaStream): Promise<void> {
    this.localStream = stream;

    if (this.config.serverType === 'srs') {
      await this.startSRSPublish();
    } else {
      await this.startOMEPublish();
    }

    // 統計情報の監視を開始
    this.startStatsMonitoring();
  }

  /**
   * WHIP配信を停止
   */
  async stop(): Promise<void> {
    await this.cleanup();
  }

  /**
   * SRS向けWHIP配信
   */
  private async startSRSPublish(): Promise<void> {
    if (!this.localStream) {
      throw new Error('Local stream not available');
    }

    // Import config dynamically to avoid circular dependencies
    const { SRS_URLS } = await import('./config');
    const whipUrl = SRS_URLS.whip(this.config.streamKey);

    // PeerConnection作成
    this.peerConnection = new RTCPeerConnection({
      iceServers: []
    });

    // イベントリスナーの設定
    this.setupPeerConnectionListeners();

    // メディアトラック追加（ビットレート設定付き）
    const videoTrack = this.localStream.getVideoTracks()[0];
    const videoSender = this.peerConnection.addTrack(videoTrack, this.localStream);

    // ビデオエンコーディング設定（解像度維持を優先）
    const parameters = videoSender.getParameters();
    if (!parameters.encodings) {
      parameters.encodings = [{}];
    }

    // ビットレート範囲を設定（本番環境対応 - 適応的品質制御）
    parameters.encodings[0] = {
      ...parameters.encodings[0],
      maxBitrate: this.config.qualityPreset.bitrate,           // 最大ビットレート
      scaleResolutionDownBy: 1.0,            // 解像度を下げない（まずビットレートで調整）
      maxFramerate: this.config.qualityPreset.frameRate,       // 最大フレームレート
      priority: 'high',                      // 高優先度
      networkPriority: 'high'                // ネットワーク優先度
    };

    // degradationPreferenceを設定（フレームレート維持優先 - 滑らかさ重視）
    parameters.degradationPreference = 'maintain-framerate';

    await videoSender.setParameters(parameters);
    console.log('ビデオエンコーディング設定完了:', parameters.encodings[0]);

    // オーディオトラックを追加
    const audioTrack = this.localStream.getAudioTracks()[0];
    if (audioTrack) {
      this.peerConnection.addTrack(audioTrack, this.localStream);
    }

    // Offer作成
    const offer = await this.peerConnection.createOffer();

    // SDPを修正して固定ビットレートを強制
    const maxBandwidthKbps = Math.round(this.config.qualityPreset.bitrate / 1000);

    // Transport Wide CCを無効化（輻輳制御を抑制）
    offer.sdp = offer.sdp!.replace(/a=extmap-allow-mixed\r\n/, '');
    offer.sdp = offer.sdp!.replace(/a=extmap:\d+ http:\/\/www\.ietf\.org\/id\/draft-holmer-rmcat-transport-wide-cc-extensions.*\r\n/g, '');

    // SDPに固定帯域を設定
    offer.sdp = offer.sdp!.replace(/m=video.*\r\n/g, (match) => {
      return match + `b=AS:${maxBandwidthKbps}\r\n` + `b=TIAS:${this.config.qualityPreset.bitrate}\r\n`;
    });

    // 初期ビットレート + キーフレーム間隔を設定（HLS安定性向上）
    const sdpLines = offer.sdp!.split('\r\n');
    const videoIndex = sdpLines.findIndex(line => line.startsWith('m=video'));
    if (videoIndex !== -1) {
      // ビデオセクションのrtcp-fbの後に設定を挿入
      for (let i = videoIndex + 1; i < sdpLines.length; i++) {
        if (sdpLines[i].startsWith('a=rtcp-fb:')) {
          // 初期ビットレートを60%に設定（安全な開始、その後段階的に上昇）
          sdpLines.splice(i + 1, 0, `a=x-google-start-bitrate:${Math.round(maxBandwidthKbps * 0.6)}`);
          // キーフレーム間隔を固定（HLSセグメント長に合わせて2秒 = 60フレーム）
          sdpLines.splice(i + 2, 0, `a=x-google-max-framerate:${this.config.qualityPreset.frameRate}`);
          console.log(`[SDP] キーフレーム間隔設定: ${this.config.qualityPreset.keyFrameInterval}フレーム (${this.config.qualityPreset.keyFrameInterval / this.config.qualityPreset.frameRate}秒)`);
          break;
        }
        if (sdpLines[i].startsWith('m=')) break; // 次のメディアセクションに到達
      }
    }
    offer.sdp = sdpLines.join('\r\n');

    console.log('[SDP] ビットレート設定:', maxBandwidthKbps, 'kbps (初期:', Math.round(maxBandwidthKbps * 0.6), 'kbps)');

    await this.peerConnection.setLocalDescription(offer);

    // WHIP APIリクエスト
    console.log('[WHIP] URL:', whipUrl);
    console.log('[WHIP] Offer SDP:', offer.sdp!.substring(0, 500));

    const response = await fetch(whipUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp'
      },
      body: offer.sdp
    });

    console.log('[WHIP] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[WHIP] Error response:', errorText);
      throw new Error(`WHIP failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Answer SDP取得
    const answerSDP = await response.text();
    console.log('[WHIP] Answer SDP:', answerSDP.substring(0, 500));

    await this.peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: answerSDP
    });

    console.log('[WHIP] 配信開始成功 - ICE接続を待機中...');

    // キーフレーム間隔を設定（GOP = 2秒 - FLV安定性向上）
    // FLVプレーヤーのデコーダ設定変更を防ぐため、定期的にキーフレームを要求
    let keyFrameRequestCount = 0;
    const keyframeIntervalMs = 2000; // 2秒ごと（srs.confのhls_fragmentと同期）

    this.keyFrameInterval = setInterval(async () => {
      if (!this.peerConnection || this.peerConnection.connectionState !== 'connected') {
        return; // cleanup()で停止するため、ここでは何もしない
      }

      try {
        const videoSender = this.peerConnection.getSenders().find(
          sender => sender.track && sender.track.kind === 'video'
        );

        if (videoSender) {
          // ブラウザがgenerateKeyFrame()をサポートしている場合はそれを使用
          if (typeof (videoSender as any).generateKeyFrame === 'function') {
            await (videoSender as any).generateKeyFrame();
            keyFrameRequestCount++;
            if (keyFrameRequestCount % 10 === 0) {
              console.log(`[KeyFrame] ${keyFrameRequestCount}回目のキーフレーム要求送信 (${keyframeIntervalMs}ms間隔)`);
            }
          } else {
            // フォールバック: setParameters()でキーフレーム生成をトリガー
            const params = videoSender.getParameters();
            if (params.encodings && params.encodings.length > 0) {
              await videoSender.setParameters(params);
              keyFrameRequestCount++;
              if (keyFrameRequestCount === 1) {
                console.log('[KeyFrame] generateKeyFrame()未サポート - setParameters()を使用');
              }
            }
          }
        }
      } catch (error) {
        console.warn('[KeyFrame] キーフレーム要求エラー:', error);
      }
    }, keyframeIntervalMs);
  }

  /**
   * OME向けWHIP配信
   */
  private async startOMEPublish(): Promise<void> {
    if (!this.localStream) {
      throw new Error('Local stream not available');
    }

    const whipUrl = `/api/ome/app/${this.config.streamKey}?direction=whip`;

    console.log('[WHIP] 配信開始:', whipUrl);

    // 既存セッションの削除を試行
    console.log('[WHIP] 既存セッションの削除を試行...');
    await this.cleanupExistingOMESession();

    // PeerConnection作成
    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        // ローカル接続を優先するが、STUNサーバーも設定
        { urls: 'stun:stun.l.google.com:19302' }
      ],
      // すべてのICE候補を収集（localhostも含む）
      iceTransportPolicy: 'all'
    });

    // イベントリスナーの設定
    this.setupPeerConnectionListeners();

    // メディアトラック追加（HLS互換コーデックを優先）
    // H.264 (video) と AAC (audio) を使用するようにコーデックを指定
    const videoTrack = this.localStream.getVideoTracks()[0];
    const audioTrack = this.localStream.getAudioTracks()[0];

    // 映像トランシーバー追加（H.264優先）
    const videoTransceiver = this.peerConnection.addTransceiver(videoTrack, {
      direction: 'sendonly',
      streams: [this.localStream]
    });

    // 音声トランシーバー追加（AAC優先）
    if (audioTrack) {
      this.peerConnection.addTransceiver(audioTrack, {
        direction: 'sendonly',
        streams: [this.localStream]
      });
      console.log('[WHIP] 🎤 Audio track added');
    } else {
      console.log('[WHIP] ⚠️ No audio track available');
    }

    // H.264コーデックを優先設定
    const videoCapabilities = RTCRtpSender.getCapabilities('video');
    if (videoCapabilities) {
      const h264Codecs = videoCapabilities.codecs.filter(codec =>
        codec.mimeType.toLowerCase() === 'video/h264'
      );
      if (h264Codecs.length > 0) {
        videoTransceiver.setCodecPreferences(h264Codecs);
        console.log('[Codec] H.264を優先設定:', h264Codecs[0].mimeType);
      } else {
        console.warn('[Codec] H.264が利用不可、デフォルトコーデックを使用');
      }
    }

    // エンコーダパラメータを設定（解像度維持、ダウンスケール防止）
    const sender = videoTransceiver.sender;
    const parameters = sender.getParameters();

    if (!parameters.encodings) {
      parameters.encodings = [{}];
    }

    // 解像度維持のためのパラメータ設定
    parameters.encodings[0].maxBitrate = this.config.qualityPreset.bitrate;  // ビットレート上限
    parameters.encodings[0].scaleResolutionDownBy = 1.0;   // ダウンスケール無効化
    // ネットワーク適応制御を抑制するために、適応的ビットレート制御を無効化
    parameters.encodings[0].networkPriority = 'high';
    parameters.encodings[0].priority = 'high';

    await sender.setParameters(parameters);
    console.log('[エンコーダ] パラメータ設定完了:', {
      maxBitrate: parameters.encodings[0].maxBitrate,
      scaleResolutionDownBy: parameters.encodings[0].scaleResolutionDownBy,
      networkPriority: parameters.encodings[0].networkPriority,
      priority: parameters.encodings[0].priority
    });

    // OPUS（音声）はHLSでサポートされているため、そのまま使用
    // ※OMEがOPUS→AAC変換を行う場合は問題なし
    console.log('[Codec] 音声トラック設定完了');

    // Offer作成
    console.log('[WHIP] Offer作成開始...');
    const offerStartTime = Date.now();
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const offerEndTime = Date.now();
    console.log(`[WHIP] Offer作成完了（${offerEndTime - offerStartTime}ms）`);

    // SDPを修正して固定ビットレートを強制（SRSと同じ処理）
    const maxBandwidthKbps = Math.round(this.config.qualityPreset.bitrate / 1000);

    // Transport Wide CCを無効化（輻輳制御を抑制）
    offer.sdp = offer.sdp!.replace(/a=extmap-allow-mixed\r\n/, '');
    offer.sdp = offer.sdp!.replace(/a=extmap:\d+ http:\/\/www\.ietf\.org\/id\/draft-holmer-rmcat-transport-wide-cc-extensions.*\r\n/g, '');

    // SDPに固定帯域を設定
    offer.sdp = offer.sdp!.replace(/m=video.*\r\n/g, (match) => {
      return match + `b=AS:${maxBandwidthKbps}\r\n` + `b=TIAS:${this.config.qualityPreset.bitrate}\r\n`;
    });

    // 初期ビットレート + 最大フレームレートを設定
    const sdpLines = offer.sdp!.split('\r\n');
    const videoIndex = sdpLines.findIndex(line => line.startsWith('m=video'));
    if (videoIndex !== -1) {
      // ビデオセクションのrtcp-fbの後に設定を挿入
      for (let i = videoIndex + 1; i < sdpLines.length; i++) {
        if (sdpLines[i].startsWith('a=rtcp-fb:')) {
          // 初期ビットレートを100%に設定（固定ビットレートに近づける）
          sdpLines.splice(i + 1, 0, `a=x-google-start-bitrate:${maxBandwidthKbps}`);
          // 最小ビットレートも設定（ビットレート低下を防ぐ）
          sdpLines.splice(i + 2, 0, `a=x-google-min-bitrate:${Math.round(maxBandwidthKbps * 0.8)}`);
          // 最大フレームレート設定
          sdpLines.splice(i + 3, 0, `a=x-google-max-framerate:${this.config.qualityPreset.frameRate}`);
          console.log(`[SDP] ビットレート設定: 開始=${maxBandwidthKbps}kbps, 最小=${Math.round(maxBandwidthKbps * 0.8)}kbps`);
          console.log(`[SDP] フレームレート設定: ${this.config.qualityPreset.frameRate}fps`);
          break;
        }
        if (sdpLines[i].startsWith('m=')) break; // 次のメディアセクションに到達
      }
    }
    offer.sdp = sdpLines.join('\r\n');

    console.log('[SDP] ビットレート設定:', maxBandwidthKbps, 'kbps (初期:', Math.round(maxBandwidthKbps * 0.6), 'kbps)');

    // WHIP APIリクエスト（リトライあり）
    console.log('[WHIP] リクエスト送信:', whipUrl);
    console.log('[WHIP] Offer SDP:', offer.sdp!.substring(0, 300) + '...');
    const fetchStartTime = Date.now();

    const response = await this.fetchWithRetry(whipUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp'
      },
      body: offer.sdp
    });

    const fetchEndTime = Date.now();
    console.log(`[WHIP] fetch完了（合計 ${fetchEndTime - fetchStartTime}ms）`);
    console.log('[WHIP] レスポンスステータス:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[WHIP] エラーレスポンス:', errorText);

      // 409 Conflictの場合は詳細情報を表示
      if (response.status === 409) {
        console.error('[WHIP] 409 Conflict - 既存ストリームが存在します');
        console.error('[WHIP] 配信停止後に再試行してください');
      }

      throw new Error(`WHIP failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // セッションURLを保存（クリーンアップ用）
    this.whipSessionUrl = response.headers.get('Location');
    console.log('[WHIP] セッションURL:', this.whipSessionUrl);

    // Answer SDP取得
    const answerSDP = await response.text();
    console.log('[WHIP] Answer SDP受信（完全版）:');
    console.log(answerSDP);

    await this.peerConnection.setRemoteDescription({
      type: 'answer',
      sdp: answerSDP
    });

    // Answer設定後、ビットレートパラメータを再設定（より確実に適用）
    console.log('[WHIP] ネゴシエーション完了後、ビットレートパラメータを再設定...');
    const videoSender = this.peerConnection.getSenders().find(s => s.track?.kind === 'video');
    if (videoSender) {
      const params = videoSender.getParameters();
      if (!params.encodings) {
        params.encodings = [{}];
      }
      params.encodings[0].maxBitrate = this.config.qualityPreset.bitrate;
      params.encodings[0].scaleResolutionDownBy = 1.0;
      params.encodings[0].networkPriority = 'high';
      params.encodings[0].priority = 'high';
      await videoSender.setParameters(params);
      console.log('[WHIP] ビットレートパラメータ再設定完了:', {
        maxBitrate: params.encodings[0].maxBitrate,
        scaleResolutionDownBy: params.encodings[0].scaleResolutionDownBy
      });
    }

    console.log('[WHIP] 配信開始成功');

    // キーフレーム間隔を設定（GOP = 2秒 - LLHLS安定性向上）
    // LLHLSセグメント長に合わせて定期的にキーフレームを要求
    let keyFrameRequestCount = 0;
    const keyframeIntervalMs = 2000; // 2秒ごと（ome.xml.templateのSegmentDurationと同期）

    this.keyFrameInterval = setInterval(async () => {
      if (!this.peerConnection || this.peerConnection.connectionState !== 'connected') {
        return;
      }

      try {
        const videoSender = this.peerConnection.getSenders().find(
          sender => sender.track && sender.track.kind === 'video'
        );

        if (videoSender) {
          // ブラウザがgenerateKeyFrame()をサポートしている場合はそれを使用
          if (typeof (videoSender as any).generateKeyFrame === 'function') {
            await (videoSender as any).generateKeyFrame();
            keyFrameRequestCount++;
            if (keyFrameRequestCount % 10 === 0) {
              console.log(`[KeyFrame] ${keyFrameRequestCount}回目のキーフレーム要求送信 (${keyframeIntervalMs}ms間隔)`);
            }
          } else {
            // フォールバック: setParameters()でキーフレーム生成をトリガー
            const params = videoSender.getParameters();
            if (params.encodings && params.encodings.length > 0) {
              await videoSender.setParameters(params);
              keyFrameRequestCount++;
              if (keyFrameRequestCount === 1) {
                console.log('[KeyFrame] generateKeyFrame()未サポート - setParameters()を使用');
              }
            }
          }
        }
      } catch (error) {
        console.warn('[KeyFrame] キーフレーム要求エラー:', error);
      }
    }, keyframeIntervalMs);
  }

  /**
   * リトライ付きfetch（OME用）
   */
  private async fetchWithRetry(url: string, options: RequestInit, maxRetries = 10): Promise<Response> {
    // OMEのWebRTCタイムアウト（30秒）に合わせた待機時間
    const delays = [2000, 3000, 5000, 10000, 10000, 10000, 10000, 10000, 10000]; // ms

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[Retry] 試行 ${attempt}/${maxRetries}: ${url}`);

        const response = await fetch(url, options);
        console.log(`[Retry] レスポンス受信: ${response.status} ${response.statusText}`);

        // 成功の場合は即座にリターン
        if (response.ok) {
          console.log(`[Retry] ✅ 成功（試行 ${attempt}/${maxRetries}）`);
          return response;
        }

        // 409 Conflict（既存ストリーム）の場合は、削除後にリトライ
        if (response.status === 409) {
          const errorBody = await response.text();
          console.warn(`[Retry] ⚠️ 409 Conflict検出（試行 ${attempt}/${maxRetries}）: ${errorBody}`);

          // URLからストリームキーを抽出
          const urlMatch = url.match(/\/app\/([^?]+)/);
          if (urlMatch && attempt < maxRetries) {
            const streamKey = urlMatch[1];
            console.log(`[Retry] ストリームキー: ${streamKey} の削除を試行`);

            // ストリームを削除
            await this.cleanupExistingOMESession();

            // 削除完了を待つ（1秒）
            const delay = 1000;
            console.log(`[Retry] ${delay}ms待機してリトライします...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;  // 次のループへ
          }

          // 最後の試行の場合は409エラーを返す
          return response;
        }

        // その他の4xxクライアントエラーは即座にリターン
        if (response.status >= 400 && response.status < 500) {
          console.error(`[Retry] クライアントエラー ${response.status} - リトライ不可`);
          return response;
        }

        // 5xxサーバーエラーの場合はリトライ
        console.warn(`[Retry] サーバーエラー ${response.status} - リトライします`);

        if (attempt < maxRetries) {
          const delay = delays[attempt - 1] || delays[delays.length - 1];
          console.log(`[Retry] ${delay}ms待機してリトライします...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

      } catch (error) {
        // ネットワークエラー（接続失敗、タイムアウトなど）
        console.error(`[Retry] ❌ 試行 ${attempt}/${maxRetries} ネットワークエラー:`, error);

        // 最後の試行の場合はエラーをスロー
        if (attempt === maxRetries) {
          throw new Error(`接続失敗 (${maxRetries}回試行): ${error instanceof Error ? error.message : String(error)}`);
        }

        // それ以外はリトライ
        const delay = delays[attempt - 1] || delays[delays.length - 1];
        console.log(`[Retry] ⏱️ ${delay}ms待機してリトライします...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error(`接続失敗: ${maxRetries}回試行しましたが接続できませんでした`);
  }

  /**
   * 既存のOME WHIPセッションを削除（409 Conflictエラー回避）
   */
  private async cleanupExistingOMESession(): Promise<void> {
    // 保存されているセッションURLがある場合はそれを使用
    if (this.whipSessionUrl) {
      try {
        // プロキシ経由でアクセスするため、/api/omeプレフィックスを追加
        const deleteUrl = `/api/ome${this.whipSessionUrl}`;
        console.log('[WHIP] 既存セッション削除試行:', deleteUrl);

        const response = await fetch(deleteUrl, { method: 'DELETE' });
        console.log('[WHIP] DELETE レスポンス:', response.status, response.statusText);

        if (response.ok) {
          console.log('[WHIP] ✅ 既存セッションを削除しました');
          this.whipSessionUrl = null;
        } else if (response.status === 404) {
          console.log('[WHIP] ℹ️ 既存セッションなし（正常）');
          this.whipSessionUrl = null;
        } else {
          const errorText = await response.text();
          console.warn('[WHIP] ⚠️ セッション削除失敗:', response.status, errorText);
        }
      } catch (error) {
        console.log('[WHIP] ℹ️ セッション削除リクエスト失敗:', error);
      }
      return;
    }

    // フォールバック: セッションURLがない場合は従来の方法
    // （初回起動時や、以前のセッションが残っている場合）
    console.log('[WHIP] セッションURL未保存 - ストリームキーで削除試行');
    const streamsToDelete = [this.config.streamKey, `${this.config.streamKey}_webrtc`];

    for (const stream of streamsToDelete) {
      try {
        const deleteUrl = `/api/ome/app/${stream}`;
        console.log('[WHIP] 既存ストリーム削除試行:', deleteUrl);

        const response = await fetch(deleteUrl, { method: 'DELETE' });
        console.log('[WHIP] DELETE レスポンス:', response.status, response.statusText);

        if (response.ok) {
          console.log('[WHIP] ✅ 既存ストリームを削除しました:', stream);
        } else if (response.status === 404) {
          console.log('[WHIP] ℹ️ 既存ストリームなし（正常）:', stream);
        } else {
          const errorText = await response.text();
          console.warn('[WHIP] ⚠️ 削除失敗:', response.status, errorText);
        }
      } catch (error) {
        console.log('[WHIP] ℹ️ 削除リクエスト失敗（エラー無視）:', error);
      }
    }
  }

  /**
   * PeerConnectionのイベントリスナーを設定
   */
  private setupPeerConnectionListeners(): void {
    if (!this.peerConnection) return;

    // ICE接続状態の監視
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('[ICE] 接続状態:', this.peerConnection?.iceConnectionState);
    };

    this.peerConnection.onconnectionstatechange = () => {
      console.log('[PC] 接続状態:', this.peerConnection?.connectionState);
      if (this.config.onConnectionStateChange && this.peerConnection) {
        this.config.onConnectionStateChange(this.peerConnection.connectionState as WHIPConnectionState);
      }
    };

    this.peerConnection.onicegatheringstatechange = () => {
      console.log('[ICE] 収集状態:', this.peerConnection?.iceGatheringState);
    };

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[ICE] ローカル候補:', event.candidate.candidate);
      } else {
        console.log('[ICE] ローカル候補の収集完了');
      }
    };
  }

  /**
   * 統計情報の監視を開始
   */
  private startStatsMonitoring(): void {
    let lastBytesSent = 0;
    let lastFramesSent = 0;
    let lastTimestamp = Date.now();

    this.statsInterval = setInterval(async () => {
      if (!this.peerConnection) return;

      try {
        const stats = await this.peerConnection.getStats();
        let outboundStats: any = null;
        let candidatePairStats: any = null;

        let inboundStats: any = null; // Phase0-3: for jitter

        stats.forEach(report => {
          if (report.type === 'outbound-rtp' && (report as any).kind === 'video') {
            outboundStats = report;
          }
          if (report.type === 'candidate-pair' && (report as any).state === 'succeeded') {
            candidatePairStats = report;
          }
          // Phase0-3: inbound-rtp for jitter collection
          if (report.type === 'inbound-rtp' && (report as any).kind === 'video') {
            inboundStats = report;
          }
        });

        if (outboundStats) {
          const now = Date.now();
          const bytesSent = outboundStats.bytesSent || 0;
          const framesSent = outboundStats.framesSent || 0;
          const deltaTime = (now - lastTimestamp) / 1000;
          const deltaBytes = bytesSent - lastBytesSent;
          const deltaFrames = framesSent - lastFramesSent;

          let bitrate = 0;
          let fps = 0;

          // ビットレート計算
          if (deltaTime > 0 && lastBytesSent > 0) {
            bitrate = Math.round((deltaBytes * 8) / (deltaTime * 1000)); // kbps
          }

          // FPS計算
          if (deltaTime > 0 && lastFramesSent > 0) {
            fps = Math.round(deltaFrames / deltaTime);
          }

          // Phase0-3: Calculate packet loss rate and jitter
          const packetsSent = outboundStats.packetsSent || 0;
          const packetsLost = outboundStats.packetsLost || 0;
          const packetLossRate = packetsSent > 0 ? (packetsLost / packetsSent) * 100 : 0;
          const jitter = inboundStats ? (inboundStats.jitter || 0) * 1000 : undefined; // convert to ms

          // 統計情報をコールバックで通知
          if (this.config.onStatsUpdate) {
            this.config.onStatsUpdate({
              bitrate,
              fps,
              resolution: outboundStats.frameWidth && outboundStats.frameHeight
                ? `${outboundStats.frameWidth}×${outboundStats.frameHeight}`
                : undefined,
              packetsSent,
              packetsLost,
              packetLossRate, // Phase0-3: Packet loss rate (%)
              jitter,         // Phase0-3: Jitter (ms)
              rtt: candidatePairStats ? candidatePairStats.currentRoundTripTime * 1000 : undefined
            });
          }

          // 詳細診断情報をコンソールに出力
          console.log('[Stats] 詳細情報:', {
            bitrate,
            targetBitrate: outboundStats.targetBitrate,
            qualityLimitationReason: outboundStats.qualityLimitationReason,
            packetsSent: outboundStats.packetsSent,
            retransmittedPacketsSent: outboundStats.retransmittedPacketsSent,
            nackCount: outboundStats.nackCount,
            pliCount: outboundStats.pliCount,
            firCount: outboundStats.firCount,
            frameWidth: outboundStats.frameWidth,
            frameHeight: outboundStats.frameHeight,
            framesPerSecond: outboundStats.framesPerSecond,
            rtt: candidatePairStats ? candidatePairStats.currentRoundTripTime * 1000 : 'N/A',
            availableOutgoingBitrate: candidatePairStats ? candidatePairStats.availableOutgoingBitrate : 'N/A'
          });

          lastBytesSent = bytesSent;
          lastFramesSent = framesSent;
          lastTimestamp = now;
        }
      } catch (err) {
        console.warn('統計情報取得エラー:', err);
      }
    }, 1000);
  }

  /**
   * クリーンアップ
   */
  private async cleanup(): Promise<void> {
    console.log('[Cleanup] クリーンアップ開始...');

    // 統計情報監視の停止
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }

    // キーフレーム間隔の停止
    if (this.keyFrameInterval) {
      clearInterval(this.keyFrameInterval);
      this.keyFrameInterval = null;
    }

    // WHIPセッション終了（OMEのみ）
    if (this.config.serverType === 'ome') {
      // cleanupExistingOMESession()が自動的にwhipSessionUrlを使用するため、これだけでOK
      await this.cleanupExistingOMESession();
    }

    // PeerConnection終了
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // ローカルストリーム停止
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    console.log('[Cleanup] クリーンアップ完了');
  }
}
